/**
 * The lookup keys that tie our plans to Stripe prices, and the billing terms
 * each plan is sold on. This is the ONLY place the app names a Stripe price.
 *
 * Amounts, currencies, product names and intervals are deliberately absent:
 * they live in Stripe and are read at request time, so a price change never
 * needs a deploy. What lives here is the contract — which lookup key means
 * "Nova, billed monthly" — because both sides have to agree on it.
 *
 * Lookup keys are movable in the Stripe dashboard (ops has repointed
 * `default_nova` before), so nothing may infer an interval from a key's name.
 * `planTermForPrice` matches on the key AND the price's real recurring
 * interval, and a price whose interval contradicts its key is dropped rather
 * than shown under the wrong term.
 *
 * Aliases: plan catalogue, lookup keys, billing term, monthly yearly plans
 */

/** How a plan is billed. Stripe intervals we sell on: month and year. */
export const BILLING_TERMS = ["monthly", "yearly"] as const;
export type BillingTerm = (typeof BILLING_TERMS)[number];

/** The Stripe `recurring.interval` each term requires. */
export const INTERVAL_BY_TERM: Record<BillingTerm, "month" | "year"> = {
  monthly: "month",
  yearly: "year",
};

/**
 * The term a recurring price bills on, read from its interval alone, or null
 * for an interval we do not sell. For a price that may carry no lookup key of
 * ours — a grandfathered subscriber's — where `planTermForPrice` cannot answer.
 */
export function billingTermForRecurring(
  recurring: { interval: string; interval_count?: number } | null | undefined,
): BillingTerm | null {
  if (!recurring || (recurring.interval_count ?? 1) !== 1) return null;
  return (
    BILLING_TERMS.find(
      (term) => INTERVAL_BY_TERM[term] === recurring.interval,
    ) ?? null
  );
}

/**
 * The term a plan is shown and sold on while `term` is selected: that term, or
 * the plan's other one when only the other has a price. A plan priced on one
 * term still shows, on its real price, rather than disappearing when the coach
 * flips the toggle.
 */
export function shownTerm(
  prices: Readonly<Record<BillingTerm, object | null>>,
  term: BillingTerm,
): BillingTerm {
  if (prices[term] !== null) return term;
  return BILLING_TERMS.find((other) => prices[other] !== null) ?? term;
}

/** Every plan we can recognise. Nova Gold is deliberately absent — undefined. */
export const PLAN_KEYS = [
  "nova",
  "nova_pro",
  "marketing_suite",
  "nova_pro_plus",
] as const;
export type PlanKey = (typeof PLAN_KEYS)[number];

/**
 * Lookup key per plan and term.
 *
 * `nova`, `nova_pro` and `nova_pro_plus` all grant Nova access, so they can
 * never be held at the same time (see the overlap guard in
 * `createSuitePlanCheckoutUrl`). `marketing_suite` grants only the GoHighLevel
 * workspace, so it stacks with `nova` but is contained by the two bundles.
 */
export const PLAN_LOOKUP_KEYS = {
  nova: { monthly: "default_nova", yearly: "default_nova_annual" },
  nova_pro: {
    monthly: "default_nova_bundle",
    yearly: "default_nova_bundle_annual",
  },
  marketing_suite: {
    monthly: "marketing_suite",
    yearly: "marketing_suite_annual",
  },
  nova_pro_plus: {
    monthly: "default_nova_proplus",
    yearly: "default_nova_proplus_annual",
  },
} as const satisfies Record<PlanKey, Record<BillingTerm, string>>;

/**
 * Whether a coach can put themselves on a plan — see it offered, and start its
 * checkout.
 *
 * NOVA Pro+ is sold to Accelerator graduates only. It is recognised when held
 * (a coach put on it by hand in Stripe gets its entitlements, its tier and its
 * GHL scopes), and it is offered and sold only on the graduate path, which
 * checks her standing on the server: `fetchNovaPlans({ includeGraduatePlans })`,
 * `createProPlusCheckoutUrl` and the plan change onto Pro+. Everything else
 * reads this flag: `fetchNovaPlans()` drops the plan, which is what the public
 * pricing grid and the suite checkout read from, and
 * {@link SUITE_PLAN_LOOKUP_KEYS} drops its keys, which is what the suite
 * checkout's input validator accepts.
 */
export const PLAN_SELF_SERVE = {
  nova: true,
  nova_pro: true,
  marketing_suite: true,
  nova_pro_plus: false,
} as const satisfies Record<PlanKey, boolean>;

/** The plans a coach can be offered and can buy for themselves. */
export const SELF_SERVE_PLAN_KEYS = PLAN_KEYS.filter(
  (plan) => PLAN_SELF_SERVE[plan],
);

/** Whether a coach may be offered this plan and start its checkout. */
export function isSelfServePlan(plan: PlanKey): boolean {
  return PLAN_SELF_SERVE[plan];
}

/** Every lookup key the app resolves, in one flat list for `prices.list`. */
export const ALL_PLAN_LOOKUP_KEYS = PLAN_KEYS.flatMap((plan) =>
  BILLING_TERMS.map((term) => PLAN_LOOKUP_KEYS[plan][term]),
);

/**
 * The `product_kind` metadata each plan's Stripe product carries.
 *
 * This is the plan's identity marker: it survives renames and price changes,
 * and it is what the subscription webhook reads to decide whether a purchase
 * grants Nova. Without it, a lookup key moved onto an unrelated product would
 * make that product look like the plan.
 */
export const PLAN_PRODUCT_KIND: Record<PlanKey, string> = {
  nova: "nova",
  nova_pro: "nova_bundle",
  marketing_suite: "marketing_suite",
  nova_pro_plus: "nova_pro_plus",
};

/** The plan a `product_kind` names, or null when it names none of ours. */
export function planKeyForProductKind(productKind: string): PlanKey | null {
  return (
    PLAN_KEYS.find((plan) => PLAN_PRODUCT_KIND[plan] === productKind) ?? null
  );
}

/**
 * Whether a product's metadata contradicts the plan it is claimed to be.
 *
 * Absent metadata is tolerated: the Marketing Suite product carries none in
 * live Stripe until the catalogue script stamps it, and this code deploys
 * before that runs. A product declaring a DIFFERENT plan's kind is rejected —
 * that is the drift worth catching, and it cannot be a deploy-order artefact.
 */
export function productKindContradictsPlan(
  plan: PlanKey,
  productKind: string | undefined,
): boolean {
  if (!productKind) return false;
  return productKind !== PLAN_PRODUCT_KIND[plan];
}

/**
 * Plans whose entitlements are fully contained by the key plan.
 *
 * NOVA Pro is Nova plus the Marketing Suite on one subscription, so selling it
 * to someone already holding either bills the same entitlement twice — and
 * selling the standalone Suite to a Pro holder does the same in reverse.
 * Nova and the standalone Suite grant disjoint entitlements, so holding both
 * is a legitimate combination, not an overlap.
 */
export const PLAN_CONTAINS = {
  nova: [],
  nova_pro: ["nova", "marketing_suite"],
  marketing_suite: [],
  // Same two entitlements as NOVA Pro. What Pro+ adds over Pro is service, not
  // a further entitlement, so nothing new is named here — and the two bundles
  // still overlap, because they contain the same things (see
  // {@link conflictingPlanKeys}).
  nova_pro_plus: ["nova", "marketing_suite"],
} as const satisfies Record<PlanKey, readonly PlanKey[]>;

/**
 * What a coach paying for `plan` is buying: the plan and everything in it.
 *
 * Every "does this plan give X" question goes through here rather than reading
 * {@link PLAN_CONTAINS} directly, so a plan that IS the entitlement and a
 * bundle that contains it answer the same way.
 */
function planEntitlements(plan: PlanKey): readonly PlanKey[] {
  return [plan, ...PLAN_CONTAINS[plan]];
}

/**
 * Every plan that must not be held alongside `plan`: any plan sharing an
 * entitlement with it. Holding one of these means a checkout for `plan` would
 * duplicate something the coach already pays for.
 *
 * Shared entitlements rather than containment in one direction: NOVA Pro and
 * NOVA Pro+ contain the same two things while neither contains the other, so a
 * containment test read them as compatible and would have sold a Pro+ coach the
 * Pro bundle on top.
 */
export function conflictingPlanKeys(plan: PlanKey): PlanKey[] {
  const held = planEntitlements(plan);
  return PLAN_KEYS.filter((candidate) =>
    planEntitlements(candidate).some((entitlement) =>
      held.includes(entitlement),
    ),
  );
}

/** Every lookup key belonging to a plan that conflicts with `plan`. */
export function conflictingLookupKeys(plan: PlanKey): string[] {
  return conflictingPlanKeys(plan).flatMap((candidate) =>
    BILLING_TERMS.map((term) => PLAN_LOOKUP_KEYS[candidate][term]),
  );
}

/**
 * The plans that grant Nova: the standalone plan and every bundle containing
 * it. Derived rather than listed, so a bundle added to {@link PLAN_CONTAINS}
 * grants Nova everywhere at once — `isNovaProduct`, the overlap guard and the
 * billing page all read this set.
 */
export const NOVA_GRANTING_PLAN_KEYS = PLAN_KEYS.filter((plan) =>
  planEntitlements(plan).includes("nova"),
);

/**
 * The `product_kind` values a Stripe product may carry to grant Nova. This is
 * what `isNovaProduct` matches on — the identity marker survives renames,
 * repricing and a lookup key being moved.
 */
export const NOVA_GRANTING_PRODUCT_KINDS: readonly string[] =
  NOVA_GRANTING_PLAN_KEYS.map((plan) => PLAN_PRODUCT_KIND[plan]);

/** Every lookup key that grants Nova, either standalone or in the bundle. */
export const NOVA_GRANTING_LOOKUP_KEYS: readonly string[] =
  NOVA_GRANTING_PLAN_KEYS.flatMap((plan) =>
    BILLING_TERMS.map((term) => PLAN_LOOKUP_KEYS[plan][term]),
  );

/** Whether a lookup key names a plan that grants Nova access. */
export function isNovaGrantingLookupKey(lookupKey: string): boolean {
  return NOVA_GRANTING_LOOKUP_KEYS.includes(lookupKey);
}

/**
 * The plans that grant the Marketing Suite: the standalone plan and the bundle
 * that contains it. Mirrors {@link NOVA_GRANTING_PLAN_KEYS} for the other
 * entitlement.
 */
export const SUITE_GRANTING_PLAN_KEYS = PLAN_KEYS.filter((plan) =>
  planEntitlements(plan).includes("marketing_suite"),
);

/**
 * Whether holding this plan leaves the coach with the Marketing Suite.
 *
 * The plan-key form of {@link grantsMarketingSuite}, and the one the membership
 * rule reads: "which plan does this set of held things give" is answered from
 * plan keys, which survive a lookup key being moved off a grandfathered price.
 */
export function planGrantsMarketingSuite(plan: PlanKey): boolean {
  return SUITE_GRANTING_PLAN_KEYS.includes(plan);
}

/** Every lookup key that grants the Marketing Suite, standalone or in the bundle. */
export const SUITE_GRANTING_LOOKUP_KEYS: readonly string[] =
  SUITE_GRANTING_PLAN_KEYS.flatMap((plan) =>
    BILLING_TERMS.map((term) => PLAN_LOOKUP_KEYS[plan][term]),
  );

/**
 * Whether a lookup key names a plan that leaves the coach with Marketing Suite
 * access — which is what the `ghl` entitlement is billed for.
 *
 * Distinct from {@link isMarketingSuiteLookupKey}, which asks whether a
 * subscription IS the standalone Suite. That is the right question when a
 * subscription's own event is handled: the bundle routes down the Nova branch
 * and never reaches the membership mapping. This one is the right question when
 * asking what a coach still holds after something else was cancelled, where a
 * surviving bundle counts.
 */
export function grantsMarketingSuite(lookupKey: string): boolean {
  return SUITE_GRANTING_LOOKUP_KEYS.includes(lookupKey);
}

/** Whether a lookup key names the STANDALONE Marketing Suite plan. */
export function isMarketingSuiteLookupKey(lookupKey: string): boolean {
  return BILLING_TERMS.some(
    (term) => PLAN_LOOKUP_KEYS.marketing_suite[term] === lookupKey,
  );
}

/**
 * The plan and term a lookup key names, or null when the key is not ours.
 * Callers that also hold the Stripe price should prefer `planTermForPrice`,
 * which additionally rejects a key pointed at the wrong interval.
 */
export function planTermForLookupKey(
  lookupKey: string,
): { plan: PlanKey; term: BillingTerm } | null {
  for (const plan of PLAN_KEYS) {
    for (const term of BILLING_TERMS) {
      if (PLAN_LOOKUP_KEYS[plan][term] === lookupKey) return { plan, term };
    }
  }
  return null;
}

/**
 * The plan and term a live price represents, or null when the price is not one
 * of ours or its interval contradicts the term its lookup key claims.
 *
 * The interval check is what makes a dashboard mistake visible as a missing
 * plan rather than a wrong price: a yearly key pointed at a monthly price would
 * otherwise render "$1,470 / month".
 *
 * `interval_count` must be 1 for the same reason. The live account already
 * carries a "every 3 months" price, and a two-year price under a yearly key
 * would be shown as an annual total and divided by 12 for its monthly
 * equivalent — both wrong by the interval count.
 */
export function planTermForPrice(price: {
  lookup_key?: string | null;
  recurring?: { interval: string; interval_count?: number } | null;
}): { plan: PlanKey; term: BillingTerm } | null {
  if (!price.lookup_key || !price.recurring) return null;
  const match = planTermForLookupKey(price.lookup_key);
  if (!match) return null;
  if ((price.recurring.interval_count ?? 1) !== 1) return null;
  return price.recurring.interval === INTERVAL_BY_TERM[match.term]
    ? match
    : null;
}

/**
 * The plans {@link SUITE_PLAN_LOOKUP_KEYS} covers: every self-serve plan that
 * grants the Marketing Suite. Derived from the catalogue at the type level as
 * well as at runtime, so the checkout's accepted keys stay literal — a caller
 * naming Nova's key, or Pro+'s, fails to compile rather than at request time.
 */
type SuitePlanKey = {
  [K in PlanKey]: K extends "marketing_suite"
    ? K
    : "marketing_suite" extends (typeof PLAN_CONTAINS)[K][number]
      ? K
      : never;
}[PlanKey];

type SelfServeSuitePlanKey = {
  [K in SuitePlanKey]: (typeof PLAN_SELF_SERVE)[K] extends true ? K : never;
}[SuitePlanKey];

const isSelfServeSuitePlan = (plan: PlanKey): plan is SelfServeSuitePlanKey =>
  planGrantsMarketingSuite(plan) && PLAN_SELF_SERVE[plan];

/**
 * Plans a coach can buy with a plain subscription checkout: the Suite-granting
 * plans we sell self-serve. Nova itself is absent because its checkout carries
 * the trial and the funnel spine (`createNovaCheckoutUrl`) and so stays the
 * single way to buy Nova; NOVA Pro+ is absent because it is not self-serve.
 */
export const SUITE_PLAN_LOOKUP_KEYS = SUITE_GRANTING_PLAN_KEYS.filter(
  isSelfServeSuitePlan,
).flatMap((plan) => BILLING_TERMS.map((term) => PLAN_LOOKUP_KEYS[plan][term]));

export type SuitePlanLookupKey = (typeof SUITE_PLAN_LOOKUP_KEYS)[number];

/** Whether a Stripe price lookup key is one of the suite plans. */
export function isSuitePlanLookupKey(
  lookupKey: string,
): lookupKey is SuitePlanLookupKey {
  return SUITE_PLAN_LOOKUP_KEYS.some((key) => key === lookupKey);
}
